import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import CartLineItem from './CartLineItem';
import CartSummary from './CartSummary';
import { useCart } from './useCart';

// 장바구니 자체이거나 장바구니 항목으로 주문을 진행 중인 화면에서는 띄우지 않는다.
const HIDDEN_PATHS = ['/cart', '/checkout', '/order/payment'];

const COLLAPSED_KEY = 'floatingCart.collapsed';

// 콘텐츠(1200px) 오른쪽 여백이 패널을 담을 만큼(약 280px) 넓을 때만 처음부터 펼친다.
const WIDE_QUERY = '(min-width: 1760px)';

function readInitialCollapsed() {
  try {
    const stored = localStorage.getItem(COLLAPSED_KEY);
    if (stored !== null) return stored === 'true';
  } catch {
    // 저장소를 못 쓰면 화면 폭으로만 판단한다.
  }
  return !window.matchMedia(WIDE_QUERY).matches;
}

/** 헤더 높이를 따라가며 패널의 top 을 맞춘다. 모바일에서는 헤더가 두 줄로 바뀐다. */
function useHeaderHeight() {
  const [height, setHeight] = useState(64);

  useEffect(() => {
    const header = document.querySelector<HTMLElement>('.header');
    if (!header) return;
    const update = () => setHeight(header.offsetHeight);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(header);
    return () => observer.disconnect();
  }, []);

  return height;
}

/**
 * 스크롤바를 숨긴 목록에서 위/아래로 가려진 내용이 있는지 알려 주고,
 * 패널 어디에서 휠을 굴리든 목록이 스크롤되게 한다. 페이지로는 휠이 넘어가지 않는다.
 */
function usePanelScroll(enabled: boolean, itemCount: number) {
  const panelRef = useRef<HTMLElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [canScrollUp, setCanScrollUp] = useState(false);
  const [canScrollDown, setCanScrollDown] = useState(false);

  useEffect(() => {
    const panel = panelRef.current;
    const list = listRef.current;
    if (!enabled || !panel || !list) return;

    const update = () => {
      setCanScrollUp(list.scrollTop > 0);
      setCanScrollDown(list.scrollTop + list.clientHeight < list.scrollHeight - 1);
    };

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      // deltaMode 1 은 줄 단위, 2 는 페이지 단위다.
      const unit = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? list.clientHeight : 1;
      list.scrollTop += e.deltaY * unit;
    };

    update();
    list.addEventListener('scroll', update, { passive: true });
    panel.addEventListener('wheel', handleWheel, { passive: false });
    const observer = new ResizeObserver(update);
    observer.observe(list);
    Array.from(list.children).forEach((child) => observer.observe(child));
    return () => {
      list.removeEventListener('scroll', update);
      panel.removeEventListener('wheel', handleWheel);
      observer.disconnect();
    };
  }, [enabled, itemCount]);

  const scrollByPage = (direction: 1 | -1) => {
    const list = listRef.current;
    if (list) list.scrollBy({ top: direction * list.clientHeight * 0.8, behavior: 'smooth' });
  };

  return { panelRef, listRef, canScrollUp, canScrollDown, scrollByPage };
}

export default function FloatingCart() {
  const location = useLocation();
  const cart = useCart();
  const headerHeight = useHeaderHeight();
  const [collapsed, setCollapsed] = useState(readInitialCollapsed);

  const setAndStoreCollapsed = (next: boolean) => {
    setCollapsed(next);
    try {
      localStorage.setItem(COLLAPSED_KEY, String(next));
    } catch {
      // 상태 기억은 편의 기능이므로 실패해도 무시한다.
    }
  };

  const hidden = HIDDEN_PATHS.some((path) => location.pathname.startsWith(path));
  const items = cart.data?.items ?? [];
  const visible = Boolean(cart.user) && !hidden && items.length > 0;
  const { panelRef, listRef, canScrollUp, canScrollDown, scrollByPage } = usePanelScroll(
    visible && !collapsed,
    items.length,
  );
  if (!visible) return null;

  if (collapsed) {
    return (
      <button
        type="button"
        className="floating-cart-toggle"
        style={{ top: headerHeight + 16 }}
        onClick={() => setAndStoreCollapsed(false)}
        aria-label="장바구니 펼치기"
      >
        <span aria-hidden>‹</span>
        장바구니
        <span className="cart-badge__count floating-cart-toggle__count">{items.length}</span>
      </button>
    );
  }

  return (
    <aside ref={panelRef} className="floating-cart" style={{ top: headerHeight }} aria-label="장바구니">
      <div className="floating-cart__head">
        <Link to="/cart" className="floating-cart__title">
          장바구니 <span className="muted">({items.length})</span>
        </Link>
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={() => setAndStoreCollapsed(true)}
          aria-label="장바구니 접기"
        >
          접기 ›
        </button>
      </div>

      <div className="floating-cart__viewport">
        <div ref={listRef} className="floating-cart__items">
          {items.map((item) => (
            <CartLineItem key={item.cartItemId} item={item} cart={cart} compact />
          ))}
        </div>
        {canScrollUp && (
          <button
            type="button"
            className="floating-cart__more floating-cart__more--up"
            onClick={() => scrollByPage(-1)}
            aria-label="위로 더 보기"
          >
            ︿
          </button>
        )}
        {canScrollDown && (
          <button
            type="button"
            className="floating-cart__more floating-cart__more--down"
            onClick={() => scrollByPage(1)}
            aria-label="아래로 더 보기"
          >
            ﹀
          </button>
        )}
      </div>

      <div className="floating-cart__summary">
        <CartSummary cart={cart} />
      </div>
    </aside>
  );
}
